import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import { debug } from "../utils";
import { TableContext } from "../index";
import { CheckboxTable } from "../view/CheckboxTable";
import { useRecentViolationsTableHelper } from "./useRecentViolationsTableHelper";
import RecentViolationsTableRow from "./RecentViolationsTableRow";
import RecentViolationsTableRowSkeleton from "./RecentViolationsTableRowSkeleton";
import ValidateHeaderMenuItem from "../rules/validate/ValidateHeaderMenuItem";
import DeviceViolationDetailsPopup from "./DeviceViolationDetailsPopup";

function RecentViolationsTable({ validationResultList }) {

    const { config } = useContext(TableContext);
    const helper = useRecentViolationsTableHelper();
    const [selectedDeviceViolation, setSelectedDeviceViolation] = useState(null);

    const getDataColumns = () => [
        { name: 'DEVICE', state: true, sortKey: 'device', onClick: () => helper.onDeviceClick() },
        { name: 'RULE NAME', state: true, sortKey: 'rule', onClick: () => helper.onRuleNameClick() },
        { name: 'DESCRIPTION', state: true, sortKey: 'details', onClick: () => helper.onDetailsClick() },
        { name: 'TYPE', state: true, sortKey: 'recordType', onClick: () => helper.onTypeClick() },
        { name: 'VIOLATION DATE', state: true, sortKey: 'startTime', onClick: () => helper.onViolationDateClick() },
        { name: 'SEVERITY', state: true, sortKey: 'severity', onClick: () => helper.onSeverityClick() },
        ...(config.showRemediated
            ? [{ name: 'REMEDIATED DATE', state: true, sortKey: 'remediatedDate', onClick: () => helper.onGroupClick() }]
            : [])
    ];

    const [hiddenHeaders, setHiddenHeaders] = useState(
        getDataColumns().filter(column => column.state !== true).map(column => column.name)
    );
    const [updatedColumns, setUpdatedColumns] = useState(getDataColumns());

    useEffect(() => {
        const nextColumns = getDataColumns().map(column => {
            const currentColumn = updatedColumns.find(item => item.name === column.name);
            return currentColumn ? { ...column, state: currentColumn.state } : column;
        });
        setUpdatedColumns(nextColumns);
        setHiddenHeaders(nextColumns.filter(column => column.state !== true).map(column => column.name));
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [config.showRemediated]);

    const columnMenu = [{
        name: <ValidateHeaderMenuItem
            key={updatedColumns.map(column => column.name).join('|')}
            updatedHiddenHeaders={hiddenHeaders}
            sendHiddenHeaders={(nextHiddenHeaders) => setHiddenHeaders([...nextHiddenHeaders])}
            dataColumns={updatedColumns}
            sendUpdatedDataColumns={(nextColumns) => setUpdatedColumns(nextColumns)}
        />,
        className: "oct_table-actions-cell-v2"
    }];

    const headers = [
        ...updatedColumns.filter(column => column.state === true),
        ...columnMenu
    ];

    const recentViolationsList = validationResultList.filter(item => item.remediated === 1);

    // Client-side search filtering
    const searchTerm = (config.searchTerm || '').toLowerCase();
    const filterBySearch = (list) => {
        if (!searchTerm) return list;
        return list.filter(item => {
            const device = (item.device || '').toLowerCase();
            const rule = (item.rule || '').toLowerCase();
            const details = (item.details || '').toLowerCase();
            const user = (item.user || '').toLowerCase();
            const severity = (item.severity || '').toLowerCase();
            const action = (item.action || '').toLowerCase();
            const source = (item.source || '').toLowerCase();
            const type = (item.violationType || (item.recordType === "DEVICE" ? "Syslog Violation" : "Rule Violation")).toLowerCase();
            return device.includes(searchTerm) || rule.includes(searchTerm) ||
                details.includes(searchTerm) || user.includes(searchTerm) ||
                severity.includes(searchTerm) || action.includes(searchTerm) ||
                source.includes(searchTerm) || type.includes(searchTerm);
        });
    };

    const displayList = config.showRemediated
        ? filterBySearch(recentViolationsList)
        : filterBySearch(validationResultList);

    return (
        <>
            <CheckboxTable className="mt-4" headers={headers}
                containsEditButton={false}
                elements={displayList}>
                {config.isLoading ? (
                    Array.from(new Array(10)).map((_, index) => (
                        <RecentViolationsTableRowSkeleton key={index} index={index} hiddenHeaders={hiddenHeaders} />
                    ))
                ) : (
                    displayList.map((data, index) => <RecentViolationsTableRow key={`${data.recordType || "RULE"}-${data.id || index}`}
                        data={data}
                        index={index}
                        hiddenHeaders={hiddenHeaders}
                        onViewDeviceViolation={setSelectedDeviceViolation} />)
                )}
            </CheckboxTable>
            {!config.isLoading && displayList.length === 0 && (
                <div className="oct_empty-state-v2">
                    <div className="oct_empty-state-title">No records found</div>
                    <div>Try adjusting your search or filters to find what you're looking for.</div>
                </div>
            )}
            {selectedDeviceViolation && (
                <DeviceViolationDetailsPopup
                    violation={selectedDeviceViolation}
                    onClose={() => setSelectedDeviceViolation(null)}
                />
            )}
        </>
    );
}

function mapStateToProps(state) {
    debug("RecentViolationsTable mapStateToProps", state);
    const recentViolationsPage = state.recentViolationsPage;
    return {
        validationResultList: recentViolationsPage.validationResultList || []
    };
}

export default connect(mapStateToProps)(RecentViolationsTable);
